// Formatting helpers. Timestamps from the API are epoch-ms numbers OR ISO strings (D1 mode), so
// every helper normalizes to a number first. JetBrains Mono is used for all of these in the UI.

export function toMs(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === "number") return value;
  const n = Number(value);
  if (!Number.isNaN(n)) return n;
  const d = Date.parse(value);
  return Number.isNaN(d) ? null : d;
}

export function fmtTime(value: number | string | null | undefined): string {
  const ms = toMs(value);
  if (ms == null) return "—";
  return new Date(ms).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function fmtRelative(value: number | string | null | undefined): string {
  const ms = toMs(value);
  if (ms == null) return "—";
  const diff = ms - Date.now();
  const abs = Math.abs(diff);
  const sign = diff >= 0 ? "in " : "";
  const suffix = diff >= 0 ? "" : " ago";
  const units: Array<[string, number]> = [
    ["d", 86_400_000],
    ["h", 3_600_000],
    ["m", 60_000],
    ["s", 1_000],
  ];
  for (const [label, size] of units) {
    if (abs >= size) return `${sign}${Math.floor(abs / size)}${label}${suffix}`;
  }
  return "now";
}

export function fmtDuration(ms: number | null | undefined): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

export function fmtPercent(value: number | null | undefined): string {
  if (value == null) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

export function fmtLagMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

/** Compose a live preview of the final target URL, mirroring `new URL(path, baseUrl)` (§11). */
export function composeUrl(baseUrl: string, path: string | null | undefined): string {
  if (!baseUrl) return "";
  if (!path) return baseUrl;
  try {
    return new URL(path, baseUrl).toString();
  } catch {
    return "(invalid URL)";
  }
}